import { Draggable } from "@hello-pangea/dnd";
import clsx from "clsx";
import Image from "next/image";

import ReactLoading from "react-loading";

import cameraPinkIcon from "@/assets/icons/photoRegister/camera-pink.svg";
import cameraIcon from "@/assets/icons/photoRegister/camera.svg";
import delPhotoIcon from "@/assets/icons/photoRegister/photo_delate.svg";

import type { FileType } from "@/hooks/common/contact/useZendesk";
import React, { useCallback } from "react";

type Props = {
  index: number;
  photoId: string;
  previewUrl: string; //写真プレビュー用のURL
  responseUrl: string; //アップロード完了時のURL
  isLoading: boolean;
  isUpLoading: boolean;
  isReadyLoading: boolean;
  isAspectRatioValid: boolean; //アスペクト比が正しいかどうか
  setReqType: React.Dispatch<React.SetStateAction<"upload" | "delete" | null>>;
  setSlideIndex: React.Dispatch<React.SetStateAction<number>>; //写真拡大表示用のスライドインデックス
  setShowPhotoExpandModal: React.Dispatch<React.SetStateAction<boolean>>; //写真拡大表示モーダルを開くかどうか
  setDeleteTarget: React.Dispatch<
    React.SetStateAction<{
      fileType: FileType;
      token: string;
    } | null>
  >; //削除対象の写真情報（お問い合わせフォーム用）
  setDeleteTargetId?: React.Dispatch<React.SetStateAction<number | null>>; //削除対象の写真ID
  handleAddUploader: () => void;
  isError?: boolean; //エラー表示
  isRequired?: boolean; //写真アップが必須かどうか
  deleteTargetId?: number | null; //削除時ブラー表示用
  capturedAt?: string | null; //撮影日時
};

/* 写真アップロード本体 */
const Uploader: React.FC<Props> = ({
  index,
  photoId,
  previewUrl,
  responseUrl,
  isLoading,
  isUpLoading,
  isReadyLoading,
  isAspectRatioValid,
  setReqType,
  setSlideIndex,
  setShowPhotoExpandModal,
  setDeleteTarget,
  setDeleteTargetId,
  handleAddUploader,
  isError,
  isRequired,
  deleteTargetId,
  capturedAt
}) => {
  const previewSrc = responseUrl || previewUrl; //写真プレビュー用のURL
  const urlPrefix = process.env.APP_PHOTO_URL || ""; //写真URLのプレフィックス
  const isContact = !setDeleteTargetId; //お問い合わせフォーム用かどうか

  const handleDelete = useCallback(
    (e: React.MouseEvent | React.KeyboardEvent) => {
      if (setDeleteTargetId) {
        setReqType("delete");
        setDeleteTargetId(Number(photoId));
      } else {
        //お問い合わせフォーム用
        setDeleteTarget({
          fileType: "photo",
          token: photoId
        });
      }
      e.stopPropagation();
    },
    [photoId, setDeleteTargetId]
  );

  const handleClick = useCallback(() => {
    if (!isUpLoading && !isLoading) {
      if (responseUrl) {
        //写真を拡大表示
        setSlideIndex(index);
        setShowPhotoExpandModal(true);
      } else {
        //写真選択画面を開く
        handleAddUploader();
      }
    }
  }, [
    isUpLoading,
    isLoading,
    responseUrl,
    index,
    setSlideIndex,
    setShowPhotoExpandModal,
    handleAddUploader
  ]);

  return (
    <Draggable
      key={photoId}
      draggableId={photoId}
      index={index + 1}
      shouldRespectForcePress={false}
      isDragDisabled={!previewSrc} //写真がアップされていない場合はドラッグ不可
    >
      {(provided, snapshot) => (
        <div
          className={clsx("flex snap-center items-center gap-8")}
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
        >
          {/* 写真アップUI */}
          <div
            className={clsx(
              "relative flex h-[9rem] w-[12rem]",
              "flex-col items-center justify-center overflow-hidden rounded-sm border",
              "hover:border-cselected active:scale-[.98]",
              isUpLoading ? "cursor-wait" : "cursor-pointer",
              isError ? "border-cErrorRed" : "border-cdisabled",
              snapshot.isDragging && "rotate-6 cursor-grab",
              isAspectRatioValid || !responseUrl || isContact //setDeleteTargetIdがない場合はお問い合わせフォーム用
                ? "bg-white"
                : "bg-[#ffeef1]"
            )}
            onClick={handleClick}
            onKeyUp={(e) => {
              if (e.key === "Enter") handleClick();
            }}
            tabIndex={0}
            title={capturedAt || ""}
          >
            {previewSrc ? (
              // 選択された写真を表示
              <div
                className={clsx(
                  isUpLoading
                    ? "animate__animated animate__fadeInUp animate__faster"
                    : "animate__animated animate__fadeIn",
                  "relative h-[9rem] w-[12rem]"
                )}
              >
                <Image
                  src={
                    previewSrc.startsWith("blob:") || isContact
                      ? previewSrc
                      : `${urlPrefix}/${previewSrc}`
                  }
                  alt="Preview"
                  className={clsx(
                    isUpLoading && "animate-pulse blur-sm",
                    deleteTargetId === Number(photoId) && "blur-sm"
                  )}
                  layout="fill"
                  objectFit={isContact ? "cover" : "contain"}
                  priority={true}
                  quality={40}
                />
                {/* アップロード中の画像加工用フィルタ */}
                {(isUpLoading || isLoading) && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <ReactLoading
                      color="#fff"
                      width="3rem"
                      height="3rem"
                      type="spin"
                    />
                  </div>
                )}
                {/* 削除ボタン */}
                {!isUpLoading && !isLoading && (
                  <div
                    className="absolute right-[0.4rem] top-4 hover:scale-110"
                    onClick={handleDelete}
                    onKeyUp={(e) => {
                      if (e.key === "Enter") handleDelete(e);
                    }}
                    tabIndex={0}
                  >
                    <Image
                      src={delPhotoIcon}
                      alt="camera"
                      width={24}
                      height={24}
                    />
                  </div>
                )}
              </div>
            ) : (
              // デフォルトUI（写真アップされていない状態）
              <div>
                {isReadyLoading && !previewSrc ? (
                  <div>
                    <ReactLoading
                      color="#BFBFBF"
                      width="3rem"
                      height="3rem"
                      type="spin"
                    />
                  </div>
                ) : (
                  <Image
                    src={
                      isRequired && index === 0 ? cameraPinkIcon : cameraIcon
                    }
                    alt="camera"
                    width={26}
                    height={24}
                  />
                )}
              </div>
            )}
            {/* 通番 */}
            <div className="absolute left-0 top-0 h-[2rem] min-w-[2rem] rounded-br-[0.4rem] bg-cdisabled text-center text-sm text-white">
              {index + 1}
            </div>
          </div>
        </div>
      )}
    </Draggable>
  );
};

export default React.memo(Uploader);
